import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';

import * as TestObservationsService from '../services/TestObservationsService';
import {TestObservationsFilter} from '../types';
import {testingApiAvailable} from '../../testing-api-integration/config';
import {
    notifyError,
    notifySuccess,
} from '../../../components/PlatformNotification';

// APPEND-ONLY module. The executor owns creation; there is NO update hook and
// there must never be one. FNP-969 adds a delete hook only — see
// services/mutations.ts.
export const testObservationsKeys = {
    all: ['test-observations'] as const,
    lists: () => [...testObservationsKeys.all, 'list'] as const,
    list: (filters: TestObservationsFilter) => [...testObservationsKeys.lists(), {filters}] as const,
    bySyntheticUser: (syntheticUserId: string) =>
        [...testObservationsKeys.all, 'by-synthetic-user', syntheticUserId] as const,
    details: () => [...testObservationsKeys.all, 'detail'] as const,
    detail: (id: string) => [...testObservationsKeys.details(), id] as const,
};

const PAGE_SIZE = 50;

export const useTestObservationsInfiniteQuery = (filters: TestObservationsFilter = {}) => {
    return useInfiniteQuery({
                                queryKey: testObservationsKeys.list(filters),
                                queryFn: ({pageParam}: { pageParam: string | null }) =>
                                    TestObservationsService.fetchRecords(filters, PAGE_SIZE, pageParam),
                                // The filters are non-key FilterExpressions server-side, so DynamoDB can
                                // hand back a near-empty page with a non-null nextToken. Paging must be
                                // driven by nextToken alone and never stop on an empty page.
                                getNextPageParam: (lastPage) => lastPage?.nextToken ?? undefined,
                                initialPageParam: null as string | null,
                                enabled: testingApiAvailable,
                            });
};

export const useTestObservationQuery = (id: string) => {
    return useQuery({
                        queryKey: testObservationsKeys.detail(id),
                        queryFn: () => TestObservationsService.fetch(id),
                        enabled: testingApiAvailable && !!id,
                    });
};

// Invalidates `all`, not just the lists: an observation also appears in the
// per-synthetic-user timeline on the Synthetic Users detail page, and a deleted row
// left in that cached panel would keep presenting evidence that no longer exists.
export const useDeleteTestObservationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
                           mutationFn: TestObservationsService.deleteRecord,
                           onSuccess: () => {
                               notifySuccess('Observation deleted');
                               queryClient.invalidateQueries({queryKey: testObservationsKeys.all});
                           },
                           onError: notifyError,
                       });
};

// Backed by the synthetic-user-observed-index GSI, not a client-side filter of the
// full list — this is the cheap path for "what did this user do?".
export const useTestObservationsBySyntheticUserQuery = (syntheticUserId?: string) => {
    return useQuery({
                        queryKey: testObservationsKeys.bySyntheticUser(syntheticUserId ?? ''),
                        queryFn: () => TestObservationsService.fetchRecordsBySyntheticUser(syntheticUserId!, PAGE_SIZE),
                        enabled: testingApiAvailable && !!syntheticUserId,
                    });
};
